import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { TAB_SLUGS, type ProjectFileKind, type ProjectFileSort, type ProjectFileView } from '@scoutaround/script-breakdown-shared';

import { api } from '../../lib/api.js';
import type { IconName } from '../Icon.js';
import type { WorkState } from '../ui/StatusPill.js';
import { extensionOf } from '../references/format.js';

/**
 * THE FILES LIST'S RULES, kept out of the components so they can be tested as rules:
 * what a search matches, how each order sorts, what each kind is drawn as and what a
 * file's checking status reads. The list is the server's whole answer for this
 * production (`GET …/files`), so narrowing it happens here, on what the server sent;
 * what a file IS (its kind) is the server's word, never re-derived from a name.
 */

export function filesKey(projectId: string) {
  return ['files', projectId] as const;
}

export function useProjectFiles(projectId: string) {
  return useQuery({
    queryKey: [...filesKey(projectId), 'list'],
    queryFn: () => api.listFiles(projectId),
    enabled: projectId !== '',
  });
}

export function useFilesMeta(projectId: string) {
  return useQuery({
    queryKey: [...filesKey(projectId), 'meta'],
    queryFn: () => api.filesMeta(projectId),
    enabled: projectId !== '',
    staleTime: 30_000,
  });
}

/** One invalidation for every read of the Files tab. */
export function useInvalidateFiles(projectId: string) {
  const queryClient = useQueryClient();
  return useCallback(() => queryClient.invalidateQueries({ queryKey: filesKey(projectId) }), [queryClient, projectId]);
}

export const FILE_KIND_ICON: Readonly<Record<ProjectFileKind, IconName>> = Object.freeze({
  PDF: 'pdf',
  DOCUMENT: 'note',
  SPREADSHEET: 'sheet',
  PRESENTATION: 'slides',
  IMAGE: 'image',
  OTHER: 'file',
});

export const SORT_LABELS: Readonly<Record<ProjectFileSort, string>> = Object.freeze({
  RECENT: 'Newest first',
  OLDEST: 'Oldest first',
  NAME: 'Name A–Z',
  SIZE: 'Largest first',
});

/** "agreement.PDF" → "PDF"; a name without one says nothing. */
export function extensionLabel(filename: string): string {
  return extensionOf(filename).toUpperCase();
}

/** Lower case, accents dropped: "Café" is found by "cafe". */
function fold(value: string): string {
  return value.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();
}

/** Every word of the search appears in the file's name or its uploader's name. */
export function matchesSearch(file: ProjectFileView, q: string): boolean {
  const words = fold(q).split(/\s+/).filter((w) => w !== '');
  if (words.length === 0) return true;
  const haystack = fold(`${file.originalFilename} ${file.uploadedBy.displayName ?? ''}`);
  return words.every((w) => haystack.includes(w));
}

const byName = new Intl.Collator('en', { sensitivity: 'base', numeric: true });

export function sortFiles(files: readonly ProjectFileView[], sort: ProjectFileSort): ProjectFileView[] {
  const list = [...files];
  const newest = (a: ProjectFileView, b: ProjectFileView) =>
    b.createdAt.localeCompare(a.createdAt) || b.id.localeCompare(a.id);
  switch (sort) {
    case 'OLDEST':
      return list.sort((a, b) => newest(b, a));
    case 'NAME':
      return list.sort((a, b) => byName.compare(a.originalFilename, b.originalFilename) || newest(a, b));
    case 'SIZE':
      return list.sort((a, b) => b.byteSize - a.byteSize || newest(a, b));
    default:
      return list.sort(newest);
  }
}

/** How many of each kind this production holds — the chips' counts, before any search. */
export function kindCounts(files: readonly ProjectFileView[]): Partial<Record<ProjectFileKind, number>> {
  const counts: Partial<Record<ProjectFileKind, number>> = {};
  for (const f of files) counts[f.kind] = (counts[f.kind] ?? 0) + 1;
  return counts;
}

/**
 * Where a file stands, in the workspace's status vocabulary. Every file is held
 * (`QUARANTINED`) until it is checked, and nothing checks files yet, so today every
 * file reads Checking; the detail says what that means. A `PASSED` file reads
 * Checked — and is still not offered for download, since no route serves one.
 */
export function fileStatus(file: Pick<ProjectFileView, 'validationStatus'>): { state: WorkState; label: string } {
  return file.validationStatus === 'PASSED'
    ? { state: 'COMPLETED', label: 'Checked' }
    : { state: 'IN_PROGRESS', label: 'Checking' };
}

/** A copyable address of one file in this production's Files tab. */
export function fileShareUrl(projectId: string, fileId: string): string {
  const origin = typeof window === 'undefined' ? '' : window.location.origin;
  return `${origin}/projects/${projectId}/${TAB_SLUGS.FILES}?file=${encodeURIComponent(fileId)}`;
}
